import { Link, Outlet, useLocation } from "react-router-dom";
import {
  MessageSquare,
  Workflow,
  BrainCircuit,
  MessageCircle,
  Phone,
  TrendingUp,
  Settings,
  LogOut,
  Lock,
  LayoutTemplate,
} from "lucide-react";
import { cn } from "../lib/utils";
import { apiFetch, getToken, setToken, clearToken } from "../lib/apiFetch";
import { useEffect, useState, useCallback } from "react";
import { WhatsAppConnectModal } from "./WhatsAppConnectModal";

const navigation = [
  { name: "Dashboard", href: "/", icon: MessageSquare },
  { name: "Conversion Flow", href: "/conversion-flow", icon: TrendingUp },
  { name: "Auto Follow-up", href: "/auto-follow-up", icon: Workflow },
  { name: "AI Knowledge Base", href: "/ai-knowledge-base", icon: BrainCircuit },
  { name: "AI Calling", href: "/calling", icon: Phone },
  { name: "WA Templates", href: "/templates", icon: LayoutTemplate },
];

export function Layout() {
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!getToken());
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [status, setStatus] = useState<string>("disconnected");
  const [pairingCode, setPairingCode] = useState("");
  const [qrCodeStr, setQrCodeStr] = useState("");
  const [loading, setLoading] = useState(false);
  const [localOwnerId, setLocalOwnerId] = useState<string>("");

  useEffect(() => {
    fetch("/api/whatsapp/owner")
      .then((r) => r.json())
      .then((d) => { if (d.ownerId) setLocalOwnerId(d.ownerId); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    async function checkStatus() {
      try {
        const res = await fetch("/api/whatsapp/status");
        if (!cancelled && res.ok) {
          const data = await res.json();
          setStatus(data.status);
          setQrCodeStr(data.qr || "");
        }
      } catch { /* server restarting */ }
    }
    checkStatus();
    const interval = setInterval(checkStatus, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isLoggedIn]);

  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        setLoginError("Incorrect password. Please try again.");
        return;
      }
      const { token } = await res.json();
      setToken(token);
      setIsLoggedIn(true);
      setPassword("");
    } catch {
      setLoginError("Connection error. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  }, [password]);

  const handleLogout = useCallback(async () => {
    await apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
    clearToken();
    setIsLoggedIn(false);
  }, []);

  const handleConnect = useCallback(
    async (phoneNumber: string, useQr: boolean) => {
      setLoading(true);
      setPairingCode("");
      setQrCodeStr("");
      try {
        const res = await apiFetch("/api/whatsapp/connect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: phoneNumber.replace(/\D/g, ""),
            ownerId: localOwnerId || "local-user",
            useQr,
          }),
        });
        const data = await res.json();
        if (data.code) setPairingCode(data.code);
      } catch (e) {
        console.error("Connection error:", e);
      } finally {
        setLoading(false);
      }
    },
    [localOwnerId]
  );

  const handleDisconnect = useCallback(async () => {
    setLoading(true);
    try {
      await apiFetch("/api/whatsapp/logout", { method: "POST" });
      setStatus("disconnected");
    } catch (e) {
      console.error("Disconnect error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  if (!isLoggedIn) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <div className="bg-white rounded-2xl shadow-lg border p-10 flex flex-col items-center gap-6 w-full max-w-sm">
          <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-7 h-7 text-white" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-900">Yogain CRM</h1>
            <p className="text-sm text-slate-500 mt-1">Enter your password to continue</p>
          </div>
          <form onSubmit={handleLogin} className="w-full flex flex-col gap-3">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                autoFocus
              />
            </div>
            {loginError && (
              <p className="text-xs text-red-500 text-center">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loginLoading || !password}
              className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
            >
              {loginLoading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full bg-slate-50 text-slate-900 font-sans">
      <WhatsAppConnectModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        status={status}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        loading={loading}
        pairingCode={pairingCode}
        qrCodeStr={qrCodeStr}
      />

      <aside className="w-64 flex flex-col border-r bg-white shrink-0">
        <div className="p-4 flex items-center gap-2 border-b">
          <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight">Yogain CRM</span>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-green-50 text-green-700 shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                <item.icon
                  className={cn(
                    "w-5 h-5",
                    isActive ? "text-green-600" : "text-slate-400"
                  )}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t">
          <button
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all w-full"
            onClick={() => {}}
          >
            <Settings className="w-5 h-5 text-slate-400" />
            Settings
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b bg-white flex items-center justify-between px-6 shrink-0">
          <h1 className="text-sm font-medium text-slate-600">Yogain CRM</h1>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowConnectModal(true)}
              className={cn(
                "px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all",
                status === "connected"
                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                  : "bg-green-600 hover:bg-green-700 text-white shadow-sm"
              )}
            >
              <MessageCircle className="w-4 h-4" />
              {status === "connected" ? "WhatsApp Connected" : "Connect WhatsApp"}
            </button>

            <button
              onClick={handleLogout}
              title="Sign out"
              className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-hidden relative">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
